import React, { createContext, useContext, useState, useEffect } from 'react';
import api from './api';
import { useAuth } from '../contexts/AuthContext';

const JobContext = createContext();

export const useJobs = () => {
  const context = useContext(JobContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobProvider');
  }
  return context;
};

export const JobProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();

  // Normalize job from backend (snake_case) to frontend shape (camelCase)
  const normalizeJob = (job) => {
    if (!job) return job;
    const backendStatus = job.status || '';
    // Map backend statuses to frontend ones
    let status = backendStatus.toLowerCase();
    if (backendStatus === 'CONVERTED') status = 'completed';

    return {
      id: job.id,
      companyName: job.company_name ?? job.companyName,
      position: job.position,
      descriptionUrl: job.description_url ?? job.descriptionUrl,
      bdName: job.bd_name ?? job.bdName,
      payRange: job.pay_range ?? job.payRange,
      jobType: job.job_type ?? job.jobType,
      status,
      resumeLink: job.resume_link ?? job.resumeLink,
      jdLink: job.jd_link ?? job.jdLink,
      createdAt: job.created_at || job.createdAt || new Date().toISOString(),
    };
  };

  const normalizeInterview = (interview, fallbackJobId) => {
    if (!interview) return interview;
    const backendStatus = interview.status || '';
    return {
      id: interview.id,
      jobId: interview.job_id || interview.jobId || fallbackJobId,
      round: interview.round,
      interviewType: interview.interview_type ?? interview.interviewType,
      dateTime: interview.date_time || interview.dateTime, // Keep as-is, frontend will handle display conversion
      duration: interview.duration,
      recruiterName: interview.recruiter_name ?? interview.recruiterName,
      resumeLink: interview.resume_link ?? interview.resumeLink,
      jdLink: interview.jd_link ?? interview.jdLink,
      meetingLink: interview.meeting_link ?? interview.meetingLink,
      attendees: interview.attendees,
      status: backendStatus.toLowerCase(),
      feedback: interview.feedback,
      interviewer: interview.interviewer,
      googleEventId: interview.google_event_id ?? interview.googleEventId,
      // Convenience field used by JobDetails; mirrors interviewer
      takenBy: interview.interviewer ?? interview.takenBy,
      notes: interview.notes,
      isFinalRound: interview.is_final_round === 'true' || interview.isFinalRound === true,
      createdAt: interview.created_at || interview.createdAt || null,
    };
  };

  // Convert frontend job shape (camelCase) to backend payload (snake_case)
  const toBackendJobPayload = (job) => {
    if (!job) return job;
    return {
      company_name: job.companyName,
      position: job.position,
      description_url: job.description || job.descriptionUrl,
      bd_name: job.bdName,
      pay_range: job.payRange,
      job_type: job.jobType,
      status: job.status || 'waiting',
      resume_link: job.resumeLink,
      jd_link: job.jdLink,
    };
  };

  // Convert frontend interview shape (camelCase) to backend payload (snake_case)
  const toBackendInterviewPayload = (interview) => {
    if (!interview) return interview;
    
    const payload = {
      job_id: interview.jobId,
      round: interview.round,
      interview_type: interview.interviewType,
      duration: interview.duration,
      recruiter_name: interview.recruiterName,
      attendees: interview.attendees,
      status: interview.status,
      notes: interview.notes,
      resume_link: interview.resumeLink,
      jd_link: interview.jdLink,
      meeting_link: interview.meetingLink,
      feedback: interview.feedback,
      interviewer: interview.interviewer ?? interview.takenBy,
      google_event_id: interview.googleEventId,
      is_final_round: interview.isFinalRound,
    };

    // Convert local datetime to ISO string for proper backend handling
    if (interview.dateTime) {
      payload.date_time = new Date(interview.dateTime).toISOString();
    }

    // Remove undefined values
    Object.keys(payload).forEach(key => payload[key] === undefined && delete payload[key]);

    return payload;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all jobs
        const jobsResponse = await api.getJobs();
        const normalizedJobs = (jobsResponse || []).map(normalizeJob);
        setJobs(normalizedJobs);

        // Fetch all interviews in one request
        // Backend automatically filters interviews by user role - no need to pass job IDs
        try {
          const allInterviewsRaw = await api.getAllInterviews();
          const allInterviews = (allInterviewsRaw || []).map(it => normalizeInterview(it));
          setInterviews(allInterviews);
        } catch (e) {
          console.error('Failed to load interviews from API', e);
          setInterviews([]);
        }
      } catch (e) {
        console.error('Failed to load jobs/interviews from API', e);
        setError(e);
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadData();
    } else {
      // When user logs out, clear data
      setJobs([]);
      setInterviews([]);
    }
  }, [isAuthenticated]);

  const addJob = (job) => {
    // Optimistically create job via API then update local state
    const create = async () => {
      const payload = toBackendJobPayload(job);
      const created = await api.createJob(payload);
      const normalized = normalizeJob(created);
      setJobs((prev) => [...prev, normalized]);
      return normalized;
    };

    // Return a promise so callers can await if they choose
    return create();
  };

  const updateJob = (id, updates) => {
    const update = async () => {
      const payload = toBackendJobPayload(updates);
      const updated = await api.updateJob(id, payload);
      const normalized = normalizeJob(updated);
      setJobs((prev) => prev.map((job) => (job.id === id ? normalized : job)));
      return normalized;
    };

    return update();
  };

  const deleteJob = (id) => {
    const remove = async () => {
      await api.deleteJob(id);
      setJobs((prev) => prev.filter((job) => job.id !== id));
      setInterviews((prev) => prev.filter((interview) => interview.jobId !== id));
    };

    return remove();
  };

  const addInterview = (interview) => {
    const create = async () => {
      const payload = toBackendInterviewPayload(interview);
      const created = await api.createInterview(interview.jobId, payload);
      const normalized = normalizeInterview(created, interview.jobId);
      setInterviews((prev) => [...prev, normalized]);
      return normalized;
    };

    return create();
  };

  const updateInterview = (id, updates) => {
    const update = async () => {
      const payload = toBackendInterviewPayload(updates);
      const updated = await api.updateInterview(id, payload);
      const normalized = normalizeInterview(updated);
      setInterviews((prev) => prev.map((interview) => (interview.id === id ? normalized : interview)));
      return normalized;
    };

    return update();
  };

  const deleteInterview = (id) => {
    const remove = async () => {
      await api.deleteInterview(id);
      setInterviews((prev) => prev.filter((interview) => interview.id !== id));
    };

    return remove();
  };

  const getJobInterviews = (jobId) => {
    return interviews.filter(interview => interview.jobId === jobId);
  };

  const getUpcomingInterviews = () => {
    const now = new Date();
    return interviews.filter(interview => new Date(interview.dateTime) > now);
  };

  const value = {
    jobs,
    interviews,
    loading,
    error,
    addJob,
    updateJob,
    deleteJob,
    addInterview,
    updateInterview,
    deleteInterview,
    getJobInterviews,
    getUpcomingInterviews
  };

  return (
    <JobContext.Provider value={value}>
      {children}
    </JobContext.Provider>
  );
};